import { alpha, Box, BoxProps, ButtonBase, CircularProgress, LinearProgress } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import PgIcon from 'Components/PgIcon';
import React, { FC } from 'react';
import { COLOR } from 'Theme/themeConstants';
import { VideoUpload } from 'Hooks/useVideoUpload';

export interface VideoUploadPreviewProps extends BoxProps {
    video: VideoUpload;
    onRemove: () => void;
    onCancel?: () => void;
    onRetry?: () => void;
}

const VideoUploadPreview: FC<VideoUploadPreviewProps> = ({
    video,
    onRemove,
    onCancel,
    onRetry,
    className,
    ...boxProps
}) => {
    const classes = useStyles({ videoId: video.id });

    const handleClick = () => {
        if (video.status === 'uploading' && onCancel) {
            onCancel();
        } else if (video.status === 'failed' && onRetry) {
            onRetry();
        } else if (video.status !== 'uploading') {
            onRemove();
        }
    };

    const isClickable = video.status !== 'uploading' || !!onCancel;

    const getOverlayContent = () => {
        if (video.status === 'uploading') {
            return (
                <Box className={classes.uploadingOverlay}>
                    <Box className={classes.progressText}>{Math.round(video.progress)}%</Box>
                </Box>
            );
        }

        if (video.status === 'failed') {
            return (
                <Box className={classes.failedOverlay}>
                    <PgIcon icon="icon-reset" styleClass={classes.retryIcon} />
                </Box>
            );
        }

        if (video.status === 'pending') {
            return (
                <Box className={classes.uploadingOverlay}>
                    <CircularProgress size={10} />
                </Box>
            );
        }

        return <PgIcon icon="icon-close" styleClass={classes.closeIcon} />;
    };

    const thumbnailSrc = video.thumbnail || '';

    return (
        <Box
            component={ButtonBase}
            className={clsx(classes.videoThumbWrap, className)}
            onClick={isClickable ? handleClick : undefined}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
            {...boxProps}
        >
            {thumbnailSrc ? (
                <img src={thumbnailSrc} alt={video.file.name} className={classes.videoThumb} />
            ) : (
                <Box className={classes.videoPlaceholder}>
                    <PgIcon icon="icon-video-on" styleClass={classes.videoIcon} />
                </Box>
            )}

            {/* Progress bar overlay for uploading state */}
            {video.status === 'uploading' && (
                <Box className={classes.progressBarContainer}>
                    <LinearProgress
                        variant="determinate"
                        value={video.progress}
                        className={classes.progressBar}
                    />
                </Box>
            )}

            {/* Overlay for hover/action states */}
            <Box
                id={`videoThumbOverlay-${video.id}`}
                className={clsx(classes.overlay, {
                    [classes.overlayVisible]: video.status === 'uploading' || video.status === 'failed' || video.status === 'pending',
                })}
            >
                {getOverlayContent()}
            </Box>
        </Box>
    );
};

const useStyles = makeStyles<Theme, { videoId: string }>((theme) => {
    return createStyles({
        videoThumbWrap: ({ videoId }) => ({
            minWidth: theme.spacing(4),
            height: theme.spacing(4),
            position: 'relative',
            borderRadius: 4,
            overflow: 'hidden',
            [`&:hover #videoThumbOverlay-${videoId}`]: {
                display: 'grid',
            },
        }),
        videoThumb: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        videoPlaceholder: {
            width: '100%',
            height: '100%',
            backgroundColor: COLOR.tertiary.music,
            display: 'grid',
            placeItems: 'center',
        },
        videoIcon: {
            fontSize: theme.spacing(2),
            color: theme.palette.common.white,
        },
        progressBarContainer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: alpha(theme.palette.common.black, 0.2),
        },
        progressBar: {
            height: '100%',
            backgroundColor: theme.palette.secondary.main,
        },
        overlay: {
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            backgroundColor: alpha(COLOR.tertiary.music, 0.4),
            display: 'none',
            placeItems: 'center',
        },
        overlayVisible: {
            display: 'grid',
        },
        uploadingOverlay: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        progressSpinner: {
            color: theme.palette.common.white,
        },
        progressText: {
            fontSize: theme.spacing(1),
            color: theme.palette.common.white,
            fontWeight: 600,
        },
        failedOverlay: {
            display: 'grid',
            placeItems: 'center',
        },
        retryIcon: {
            fontSize: theme.spacing(1.5),
            color: theme.palette.common.black,
        },
        closeIcon: {
            fontSize: theme.spacing(1),
            color: theme.palette.common.black,
            WebkitTextStrokeWidth: '1px',
        },
    });
});

export default VideoUploadPreview;
